/* SalesProgressionForecastPage.jsx — iter-208 canonical Sales Progression.
 *
 *  Forecasting + Revenue intelligence + Operational pressure forecasting.
 *  Strictly NOT a Deal Rooms clone.
 *
 *  Deal Rooms    = live transaction coordination.
 *  Sales Prog.   = predictive forecasting telemetry.
 *
 *  Layout
 *    Header (workspace pill · search · filters · saved views · date range · create CTA)
 *    KPI strip (5 forecasting cards w/ delta vs last 4 weeks)
 *    Today's operational focus strip
 *    Forecast tabs (Exchange · Completion · Pipeline health · Revenue · Chain map)
 *      + Group by · View · ⋮
 *    Weekly forecasting timeline grid
 *      - vertical TODAY line + chip
 *      - per-row: thumb · property · tenure pill · fee · timeline bar
 *                 (Exchange pill → connector → Completion pill) · confidence
 *                 ring · fee · kebab
 *    Right rail (Exchange pressure donut · Upcoming exchanges · Recent activity)
 *    Legend footer + Load more + relative-time hint.
 *
 *  Inspired by: Linear roadmap planning · airline scheduling · F1 operational
 *  telemetry · Stripe operational clarity. NEVER CRM / board / dashboard clutter.
 */
import React, { useMemo, useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Calendar, Search as SearchIcon, Filter, Bookmark, Plus, MoreHorizontal,
  ChevronDown, ArrowUp, ArrowDown, Star, AlertTriangle, FileText,
  Mail, Shield, Check, Clock, Sparkles, ArrowRight, CircleDot, Home as HomeIcon,
} from "lucide-react";

import { Sidebar } from "../components/Sidebar";
import { useChainStore, openCreateDeal } from "../data/chainStore";
import { PageHeader, WORKSPACE_SHELL_CLASS } from "../components/ui/PageHeader";
import { useAuth } from "../data/authStore";
import { useOperatorPrefs } from "../hooks/useOperatorPrefs";
import { relTimeFromIso } from "../lib/relativeTime";
import { PROPERTY_FALLBACK_SVG, onPropertyImgError } from "../lib/propertyImage";
import { resolveAgreedFeeGBP } from "../lib/feeFormat";

const FALLBACK_THUMB = PROPERTY_FALLBACK_SVG;

/* ─── helpers ───────────────────────────────────────────────────────── */
const _MS_PER_DAY = 86_400_000;
const _hash = (s) => { let h = 0; for (let i = 0; i < (s || "").length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0; return Math.abs(h); };

const startOfWeek = (d) => {
  // Monday-anchored week start (ISO).
  const dt = new Date(d);
  const day = (dt.getDay() + 6) % 7;        // Mon=0 … Sun=6
  dt.setHours(0, 0, 0, 0);
  dt.setDate(dt.getDate() - day);
  return dt;
};

const addDays = (d, n) => {
  const dt = new Date(d);
  dt.setDate(dt.getDate() + n);
  return dt;
};

const fmtWeekHeader = (d) => d.toLocaleDateString("en-GB", { day: "numeric", month: "short" }).toUpperCase();
const fmtPillDate   = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "Not set");
// Pence shown only when present, so £6,562.50 matches the Deal Room / Revenue card exactly.
const fmtMoney      = (n) => {
  const v = Math.round((Number(n) || 0) * 100) / 100;
  return "£" + v.toLocaleString("en-GB", Number.isInteger(v) ? {} : { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/* The horizon: today's Monday + 5 following weeks + a "30+ JUN" beyond
 * bucket. Week 0 = this week, Week 5 = beyond. */
const NUM_WEEKS = 6;

function weekColFor(iso, anchor) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  const a = anchor.getTime();
  const diffWeeks = Math.floor((t - a) / (_MS_PER_DAY * 7));
  if (diffWeeks < 0) return 0;            // overdue → clamp to "This week" column
  if (diffWeeks >= NUM_WEEKS - 1) return NUM_WEEKS - 1; // beyond
  return diffWeeks;
}

const TENURE_PALETTE = "bg-slate-50 text-slate-600 ring-1 ring-slate-200/70";

/* iter-221B — Stage + status semantics mapped from the deal's
 * canonical stage enum. The Sales Progression page surfaces three
 * operational stages with calm status notes per the visual target. */
const STAGE_META = {
  // chip palette · status default · friendly stage label
  exchanged:      { chip: "bg-emerald-50 text-emerald-700 ring-emerald-200/70",  label: "Exchanged",     defaultNote: "Moving Well" },
  completed:      { chip: "bg-emerald-50 text-emerald-700 ring-emerald-200/70",  label: "Completed",     defaultNote: "Closed" },
  exchange_ready: { chip: "bg-amber-50 text-amber-700 ring-amber-200/70",        label: "Pre-Exchange",  defaultNote: "Attention" },
  enquiries:      { chip: "bg-amber-50 text-amber-700 ring-amber-200/70",        label: "Pre-Exchange",  defaultNote: "Attention" },
  mortgage_offer: { chip: "bg-amber-50 text-amber-700 ring-amber-200/70",        label: "Pre-Exchange",  defaultNote: "Attention" },
  searches:       { chip: "bg-amber-50 text-amber-700 ring-amber-200/70",        label: "Conveyancing",  defaultNote: "At Risk" },
  solicitors:     { chip: "bg-amber-50 text-amber-700 ring-amber-200/70",        label: "Conveyancing",  defaultNote: "At Risk" },
  survey:         { chip: "bg-amber-50 text-amber-700 ring-amber-200/70",        label: "Conveyancing",  defaultNote: "At Risk" },
  mortgage_app:   { chip: "bg-amber-50 text-amber-700 ring-amber-200/70",        label: "Conveyancing",  defaultNote: "At Risk" },
  memo:           { chip: "bg-violet-50 text-violet-700 ring-violet-200/70",     label: "Memo",          defaultNote: "Moving" },
  aml:            { chip: "bg-violet-50 text-violet-700 ring-violet-200/70",     label: "AML",           defaultNote: "Moving" },
  offer:          { chip: "bg-violet-50 text-violet-700 ring-violet-200/70",     label: "Offer Agreed",  defaultNote: "Moving" },
};
const STAGE_DEFAULT = { chip: "bg-slate-50 text-slate-600 ring-slate-200/70", label: "In progress", defaultNote: "Moving" };

/* Calm dot tone per status note. */
const NOTE_TONE = {
  "Moving Well":  { dot: "bg-emerald-500", text: "text-emerald-600" },
  "Moving":       { dot: "bg-violet-500",  text: "text-violet-600" },
  "Attention":    { dot: "bg-amber-500",   text: "text-amber-600" },
  "At Risk":      { dot: "bg-rose-500",    text: "text-rose-600" },
  "Closed":       { dot: "bg-slate-400",   text: "text-slate-500" },
};

/* Confidence label per tone (matches the visual target). */
const CONF_LABEL = {
  high:   { label: "Stable",      sub: "On track" },
  medium: { label: "Medium Risk", sub: "Watching" },
  low:    { label: "Drifting",    sub: "At risk" },
  none:   { label: "Pending",     sub: "—" },
};

const CONF_TONES = {
  high:   { ring: "ring-emerald-300", bg: "bg-emerald-50", text: "text-emerald-700", dot: "bg-emerald-500", barRing: "ring-emerald-200/80", barBg: "bg-emerald-50/70", icon: "text-emerald-500" },
  medium: { ring: "ring-amber-300",   bg: "bg-amber-50",   text: "text-amber-700",   dot: "bg-amber-500",   barRing: "ring-amber-200/80",   barBg: "bg-amber-50/70",   icon: "text-amber-500"   },
  low:    { ring: "ring-rose-300",    bg: "bg-rose-50",    text: "text-rose-700",    dot: "bg-rose-500",    barRing: "ring-rose-200/80",    barBg: "bg-rose-50/70",    icon: "text-rose-500"    },
  none:   { ring: "ring-slate-200",   bg: "bg-slate-50",   text: "text-slate-500",   dot: "bg-slate-300",   barRing: "ring-slate-200/70",   barBg: "bg-slate-50/70",   icon: "text-slate-400"   },
};

function confidenceTone(score) {
  if (score == null) return "none";
  if (score >= 70) return "high";
  if (score >= 40) return "medium";
  return "low";
}

/* ─── annotateForecast — every datum the row needs ─────────────────── */
function annotateForecast(deal, anchor) {
  const id = deal?.id || "";
  const seed = _hash(id);

  /* tenure — Freehold / Leasehold ONLY. */
  const tenureRaw = String(deal?.tenure || "").toLowerCase();
  const tenure = tenureRaw === "freehold" || tenureRaw === "leasehold"
    ? tenureRaw
    : (seed % 3 === 0 ? "leasehold" : "freehold");  // deterministic fallback for demo data

  /* Canonical target dates only — never invented. Undated deals are left out of the forecast. */
  const exchangeIso = deal?.targetExchange || deal?.target_exchange || null;
  const completionIso = deal?.targetCompletion || deal?.target_completion || null;

  /* fee — canonical resolver (same as Deal Rooms / Revenue / Forecasting Centre). */
  const value = Number(deal?.value || 0);
  const fee = resolveAgreedFeeGBP(deal) || 0;

  /* confidence — canonical exchange probability only (same source as the Forecasting Centre); null = not assessed. */
  const _prob = deal?.exchangeProbability ?? deal?.exchange_probability;
  const confidence = typeof _prob === "number" ? Math.max(0, Math.min(100, Math.round(_prob))) : null;

  const xcWeek = weekColFor(exchangeIso, anchor);
  const ccWeek = weekColFor(completionIso, anchor);

  /* iter-208P — calm fallbacks. When the demo / seeded deal carries
   * neither a name nor an address, surface a single muted "Deal · <id6>"
   * line rather than the previous noisy pair. */
  const rawName    = deal?.name || "";
  const rawAddress = deal?.address || "";
  const hasName    = !!rawName.trim();
  const hasAddress = !!rawAddress.trim();
  const fallbackId = id ? id.slice(0, 6).toUpperCase() : "—";
  const name    = hasName    ? rawName    : hasAddress ? rawAddress : `Deal · ${fallbackId}`;
  const address = hasAddress ? rawAddress : null;

  return {
    id,
    name,
    address,
    thumb: deal?.thumbnail || deal?.imageUrl || FALLBACK_THUMB,
    tenure,
    value,
    fee,
    exchangeIso,
    completionIso,
    confidence,
    confTone: confidenceTone(confidence),
    xcWeek,
    ccWeek,
    /* iter-221B — visual-target additions */
    stageMeta: STAGE_META[String(deal?.stage || "").toLowerCase()] || STAGE_DEFAULT,
    statusNote: (() => {
      // Note follows confidence — calm pivot from "Moving Well" to
      // "Attention" to "At Risk" so the note + donut never disagree.
      const sm = STAGE_META[String(deal?.stage || "").toLowerCase()] || STAGE_DEFAULT;
      if (sm.label === "Exchanged" || sm.label === "Completed") return sm.defaultNote;
      const t = confidenceTone(confidence);
      if (t === "high")   return "Moving Well";
      if (t === "medium") return "Attention";
      if (t === "low")    return "At Risk";
      return sm.defaultNote;
    })(),
    parties: (() => {
      // Buyer solicitor ↔ Seller solicitor + N overflow. Pull from
      // the deal's `participants` array when present; otherwise drop
      // calm seeded labels so the visual target reads correctly.
      const ps = Array.isArray(deal?.participants) ? deal.participants : [];
      const findOrg = (type) => {
        const p = ps.find((x) => (x?.participant_type || x?.role) === type);
        return p?.organisation_name || p?.full_name || null;
      };
      // Linked solicitor participants only; never a seeded firm name.
      const a = findOrg("buyer_solicitor")  || "Not assigned";
      const b = findOrg("seller_solicitor") || "Not assigned";
      const extra = Math.max(0, ps.length - 2);
      return { buyerSol: a, sellerSol: b, extra };
    })(),
    /* Drift risk — surfaces when confidence is amber+ and exchange is
     * within the horizon. Surfaces a "Likely <date>" projection that
     * lands halfway between the planned exchange and completion. */
    driftRisk: (() => {
      const t = confidenceTone(confidence);
      if (t !== "medium" && t !== "low") return null;
      if (!exchangeIso || !completionIso) return null;
      const halfway = new Date(
        (new Date(exchangeIso).getTime() + new Date(completionIso).getTime()) / 2,
      ).toISOString();
      return { likelyIso: halfway };
    })(),
  };
}

/* ─── Header chrome ────────────────────────────────────────────────── */
function ForecastHeader({ search, setSearch, dateRangeLabel }) {
  return (
    <div className="hidden md:block">
      <PageHeader
        icon={Calendar}
        title="Sales Progression"
        description="Forecast exchanges, completions and revenue across your pipeline."
        testid="sp-fc-header"
        titleTestid="sp-fc-title"
        actions={(
          <>
            <div className="relative">
              <SearchIcon className="h-3.5 w-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search deals, addresses, people…"
                className="pl-9 pr-9 py-2 text-[12.5px] rounded-lg ring-1 ring-slate-200 focus:ring-2 focus:ring-violet-300 focus:outline-none bg-white w-[260px]"
                data-testid="sp-fc-search"
              />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[9.5px] font-bold tracking-tight rounded bg-slate-100 text-slate-500 px-1.5 py-0.5">⌘K</span>
            </div>
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-lg ring-1 ring-slate-200 hover:bg-slate-50 bg-white px-3 py-2 text-[12.5px] font-medium text-slate-700 cf-press"
              data-testid="sp-fc-filters"
            >
              <Filter className="h-3.5 w-3.5" /> Filters
              <span className="text-[10px] font-bold tabular-nums rounded-full bg-violet-100 text-violet-700 px-1.5 min-w-[18px] h-[18px] inline-flex items-center justify-center">2</span>
            </button>
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-lg ring-1 ring-slate-200 hover:bg-slate-50 bg-white px-3 py-2 text-[12.5px] font-medium text-slate-700 cf-press"
              data-testid="sp-fc-saved-views"
            >
              <Bookmark className="h-3.5 w-3.5" /> Saved views
              <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
            </button>
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-lg ring-1 ring-slate-200 hover:bg-slate-50 bg-white px-3 py-2 text-[12.5px] font-medium text-slate-700 cf-press"
              data-testid="sp-fc-date-range"
            >
              <Calendar className="h-3.5 w-3.5 text-slate-400" /> {dateRangeLabel}
              <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
            </button>
            <button
              type="button"
              onClick={() => openCreateDeal({})}
              className="inline-flex items-center gap-1.5 rounded-lg bg-violet-600 hover:bg-violet-700 text-white px-3.5 py-2 text-[12.5px] font-semibold shadow-[0_4px_14px_-6px_rgba(124,58,237,0.55)] cf-press"
              data-testid="sp-fc-create"
            >
              <Plus className="h-3.5 w-3.5" /> New Deal Room
            </button>
          </>
        )}
      />
    </div>
  );
}

/* ─── KPI strip — 5 forecasting cards ──────────────────────────────── */
function KpiStrip({ kpis }) {
  /* Each card carries an `id`, label, value, sub, delta string,
   * delta tone, and a calm icon tile. */
  const cards = [
    { id: "exchanges",    label: "Forecast exchanges",   value: kpis.exchanges,        sub: "in next 4 weeks", delta: `${kpis.exchangesDelta >= 0 ? "↗" : "↘"} ${Math.abs(kpis.exchangesDelta)} vs last 4 weeks`, deltaTone: kpis.exchangesDelta >= 0 ? "emerald" : "rose", tile: "bg-violet-50 text-violet-500",  icon: Calendar },
    { id: "completions",  label: "Forecast completions", value: kpis.completions,      sub: "in next 4 weeks", delta: `${kpis.completionsDelta >= 0 ? "↗" : "↘"} ${Math.abs(kpis.completionsDelta)} vs last 4 weeks`, deltaTone: kpis.completionsDelta >= 0 ? "emerald" : "rose", tile: "bg-violet-50 text-violet-500",  icon: Calendar },
    { id: "projected",    label: "Projected fees",       value: fmtMoney(kpis.projectedFees),    sub: "in next 4 weeks", delta: `${kpis.projectedFeesDelta >= 0 ? "↗" : "↘"} ${fmtMoney(Math.abs(kpis.projectedFeesDelta))} vs last 4 weeks`, deltaTone: "emerald", tile: "bg-emerald-50 text-emerald-600", icon: "pound" },
    { id: "at-risk",      label: "Fees at risk",         value: fmtMoney(kpis.feesAtRisk),       sub: `${kpis.feesAtRiskDeals} deals`, delta: `${kpis.feesAtRiskDelta >= 0 ? "↗" : "↘"} ${fmtMoney(Math.abs(kpis.feesAtRiskDelta))} vs last 4 weeks`, deltaTone: "amber", tile: "bg-amber-50 text-amber-500", icon: AlertTriangle },
    { id: "weighted",     label: "Weighted confidence",  value: `${kpis.weightedConfidence}%`,   sub: "of forecast",    delta: `${kpis.weightedConfidenceDelta >= 0 ? "↗" : "↘"} ${Math.abs(kpis.weightedConfidenceDelta)}% vs last 4 weeks`, deltaTone: "violet", tile: "bg-violet-50 text-violet-500", icon: "trend" },
  ];

  const deltaCls = (tone) =>
    tone === "emerald" ? "text-emerald-600" :
    tone === "rose"    ? "text-rose-600"    :
    tone === "amber"   ? "text-amber-600"   :
                         "text-violet-600";

  return (
    <div
      className="hidden md:grid grid-cols-2 lg:grid-cols-5 gap-3 mb-4"
      data-testid="sp-fc-kpi-strip"
    >
      {cards.map((c) => {
        const Icon = c.icon === "pound" ? null : c.icon === "trend" ? null : c.icon;
        return (
          <div
            key={c.id}
            className="rounded-2xl bg-white px-4 py-3"
            style={{ boxShadow: "inset 0 0 0 1px rgba(226,232,240,0.6), 0 1px 2px rgba(15,23,42,0.025)" }}
            data-testid={`sp-fc-kpi-${c.id}`}
          >
            <div className="flex items-start gap-2.5">
              <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${c.tile}`}>
                {Icon
                  ? <Icon className="h-3.5 w-3.5" strokeWidth={2} />
                  : c.icon === "pound"
                    ? <span className="text-[12.5px] font-bold leading-none">£</span>
                    : <span className="text-[12.5px] font-bold leading-none">↗</span>}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[10.5px] text-slate-500 font-medium tracking-tight truncate">{c.label}</div>
                <div className="mt-0.5 text-[20px] font-bold tabular-nums leading-none text-slate-900 truncate">{c.value}</div>
                <div className="mt-1 text-[10.5px] text-slate-500 truncate">{c.sub}</div>
              </div>
            </div>
            <div className={`mt-1.5 text-[10px] font-semibold ${deltaCls(c.deltaTone)} truncate`} data-testid={`sp-fc-kpi-delta-${c.id}`}>
              {c.delta}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ─── Today's operational focus strip ──────────────────────────────── */
function FocusStrip({ stats }) {
  return (
    <div
      className="hidden md:flex items-center justify-between gap-4 rounded-2xl bg-violet-50/30 px-5 py-2.5 mb-5"
      style={{ boxShadow: "inset 0 0 0 1px rgba(196,181,253,0.25)" }}
      data-testid="sp-fc-focus-strip"
    >
      <div className="flex items-center gap-x-5 gap-y-2 flex-wrap">
        <span className="text-[11.5px] font-semibold text-violet-700 tracking-tight">Today&apos;s operational focus</span>
        <span className="inline-flex items-center gap-1.5 text-[11.5px] text-slate-700">
          <span className="h-1.5 w-1.5 rounded-full bg-rose-500" aria-hidden />
          <span className="font-semibold tabular-nums">{stats.needAttention}</span> deals need attention
        </span>
        <span className="inline-flex items-center gap-1.5 text-[11.5px] text-slate-700">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden />
          <span className="font-semibold tabular-nums">{stats.exchangesThisWeek}</span> exchanges this week
        </span>
        <span className="inline-flex items-center gap-1.5 text-[11.5px] text-slate-700">
          <span className="h-1.5 w-1.5 rounded-full bg-amber-500" aria-hidden />
          <span className="font-semibold tabular-nums">{stats.silentCounterparties}</span> silent counterparties
        </span>
      </div>
      <Link
        to="/insights"
        className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-violet-700 hover:text-violet-900 shrink-0"
        data-testid="sp-fc-focus-view-all"
      >
        View all intelligence <ArrowRight className="h-3 w-3" />
      </Link>
    </div>
  );
}

/* ─── Forecast tabs + Group/View controls ──────────────────────────── */
const FORECAST_TABS = [
  { id: "exchange",   label: "Exchange forecast" },
  { id: "completion", label: "Completion forecast" },
  { id: "pipeline",   label: "Pipeline health" },
  { id: "revenue",    label: "Revenue forecast" },
  { id: "chain",      label: "Chain map" },
];

function ForecastTabs({ activeTab, setActiveTab, groupBy, setGroupBy, viewMode }) {
  return (
    <div className="hidden md:flex items-center justify-between mb-4" data-testid="sp-fc-tabs">
      <nav className="flex items-center gap-5">
        {FORECAST_TABS.map((t) => {
          const isActive = activeTab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setActiveTab(t.id)}
              className={`relative pb-2 text-[12.5px] font-semibold tracking-tight transition-colors ${
                isActive ? "text-violet-700" : "text-slate-500 hover:text-slate-800"
              }`}
              data-testid={`sp-fc-tab-${t.id}`}
              aria-pressed={isActive}
            >
              {t.label}
              {isActive && (
                <span className="absolute left-0 right-0 -bottom-px h-[2px] bg-violet-600 rounded-full" />
              )}
            </button>
          );
        })}
      </nav>
      {/* iter-232 — Pre-pilot simplification: the "Group by:" and
          "View:" controls (plus the overflow kebab) were removed
          to declutter the forecast tabs bar. The canonical view
          mode + grouping are driven by the tabs themselves and
          the existing Filters control above; the operator never
          had to reach for a second control surface here. */}
    </div>
  );
}

/* ─── Week column header ───────────────────────────────────────────── */
function WeekHeader({ anchor, weekSummaries, weekFees, totalActive, totalFees }) {
  const headers = Array.from({ length: NUM_WEEKS }).map((_, i) => {
    const d = addDays(anchor, i * 7);
    const summary = weekSummaries[i] || { exc: 0, comp: 0 };
    const fees = weekFees[i] || 0;
    const label =
      i === 0 ? "This week" :
      i === 1 ? "Next week" :
      i === NUM_WEEKS - 1 ? "Beyond 4 weeks" :
      `In ${i} weeks`;
    return { date: d, label, summary, fees, isLast: i === NUM_WEEKS - 1 };
  });

  return (
    <div
      className="grid items-stretch gap-3 px-5 py-4 rounded-t-2xl bg-white border-b border-slate-100"
      style={{ gridTemplateColumns: `minmax(220px,260px) 130px repeat(${NUM_WEEKS}, minmax(0,1fr)) 110px 88px 28px` }}
      data-testid="progression-forecast-lane"
    >
      {/* iter-221B — Week-commencing anchor cell. */}
      <div className="flex flex-col justify-center" data-testid="progression-forecast-anchor">
        <div className="text-[9.5px] font-bold uppercase tracking-[0.12em] text-slate-400">Week commencing</div>
        <div className="text-[15.5px] text-slate-900 font-bold tracking-tight mt-1 leading-tight">
          {anchor.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
        </div>
        <div className="text-[10.5px] text-slate-500 tabular-nums mt-1">
          {totalActive} active · {fmtMoney(totalFees)} fees
        </div>
      </div>
      {/* Empty stage column header */}
      <div aria-hidden />
      {headers.map((h, i) => (
        <div
          key={i}
          className="relative flex flex-col items-center text-center justify-start pt-1"
          data-testid={`progression-forecast-week-${i}`}
        >
          <div className="text-[11px] font-bold tabular-nums text-slate-800 tracking-tight">
            {h.isLast ? "30 JUN+" : fmtWeekHeader(h.date)}
          </div>
          <div className="text-[10px] text-slate-400 mt-0.5">{h.label}</div>
          <div className="text-[14px] font-bold text-slate-900 tabular-nums mt-1.5 leading-none" data-testid={`progression-forecast-week-${i}-count`}>
            {h.summary.exc + h.summary.comp}
          </div>
          <div className="text-[10px] text-slate-500 tabular-nums mt-0.5" data-testid={`progression-forecast-week-${i}-fees`}>
            {fmtMoney(h.fees)}
          </div>
        </div>
      ))}
      <div className="text-[9.5px] font-bold uppercase tracking-[0.1em] text-slate-400 text-center self-center">
        Confidence
      </div>
      <div className="text-[9.5px] font-bold uppercase tracking-[0.1em] text-slate-400 text-right self-center">
        Fee
        <div className="text-[9px] text-slate-300 font-medium mt-0.5">(Forecast)</div>
      </div>
      <div aria-hidden />
    </div>
  );
}

/* iter-221B — column subheaders row (Property & Parties · Stage ·
 * (timeline) · Confidence · Fee Forecast) matches the visual target. */
function ColumnSubheaders() {
  return (
    <div
      className="grid items-center gap-3 px-5 py-2 bg-slate-50/40 border-b border-slate-100"
      style={{ gridTemplateColumns: `minmax(220px,260px) 130px repeat(${NUM_WEEKS}, minmax(0,1fr)) 110px 88px 28px` }}
      data-testid="progression-forecast-subheaders"
    >
      <div className="text-[9.5px] font-bold uppercase tracking-[0.12em] text-slate-400">Property &amp; Parties</div>
      <div className="text-[9.5px] font-bold uppercase tracking-[0.12em] text-slate-400">Stage</div>
      <div style={{ gridColumn: `span ${NUM_WEEKS}` }} aria-hidden />
      <div className="text-[9.5px] font-bold uppercase tracking-[0.12em] text-slate-400 text-center">Confidence</div>
      <div className="text-[9.5px] font-bold uppercase tracking-[0.12em] text-slate-400 text-right">Fee (Forecast)</div>
      <div aria-hidden />
    </div>
  );
}

/* ─── Today line (vertical) + chip ─────────────────────────────────── */
function TodayLine({ leftPx }) {
  // Renders a thin violet vertical line + a compact "TODAY" chip at
  // the top of the timeline grid. Positioned absolutely over the grid.
  return (
    <div
      className="pointer-events-none absolute top-0 bottom-0 z-10"
      style={{ left: leftPx }}
      data-testid="sp-fc-today-line"
    >
      <div className="absolute -top-3 -left-7 px-2 py-0.5 rounded-md bg-violet-600 text-white text-[9px] font-bold tracking-wide shadow-[0_4px_10px_-4px_rgba(124,58,237,0.55)]">TODAY</div>
      <div
        className="h-full w-px bg-violet-400/70"
        style={{ boxShadow: "0 0 6px 0 rgba(124,58,237,0.35)" }}
      />
    </div>
  );
}

/* ─── Timeline bar (Exchange → Completion pills + connector) ────────── */
function TimelineBar({ forecast }) {
  const tone = CONF_TONES[forecast.confTone];
  const xc = forecast.xcWeek;
  const cc = Math.max(xc, forecast.ccWeek);  // never reverse-spans
  const sameWeek = xc === cc;
  const drift = forecast.driftRisk;
  // Map confidence tone → connector tone (dotted when drifting).
  const isRisk = forecast.confTone === "low" || forecast.confTone === "medium";

  // The bar occupies the row's week cells. Grid columns are 1-indexed
  // and the bar starts at column (xc + 3) because col 1 = property,
  // col 2 = stage, and weeks are cols 3..3+NUM_WEEKS.
  return (
    <div
      className="relative h-full flex items-center"
      style={{ gridColumn: `${xc + 3} / ${cc + 4}` }}
      data-testid={`sp-fc-bar-${forecast.id}`}
    >
      {/* Exchange pill with date below */}
      <div className="flex flex-col items-center shrink-0" data-testid={`sp-fc-bar-exchange-${forecast.id}`}>
        <span className={`inline-flex items-center gap-1 text-[10px] font-semibold ${tone.text} leading-tight`}>
          <span className={`w-2.5 h-2.5 rounded-full ${tone.dot} ring-2 ring-white shadow-[0_0_0_1px_rgba(15,23,42,0.06)]`} aria-hidden />
          Exchange
        </span>
        <span className="text-[10px] text-slate-500 tabular-nums mt-0.5">
          {fmtPillDate(forecast.exchangeIso)}
        </span>
      </div>

      {/* Connector */}
      {!sameWeek && (
        <div className="relative flex-1 h-6 mx-2 flex items-center" data-testid={`sp-fc-bar-connector-${forecast.id}`}>
          {/* line */}
          <div
            className={`absolute inset-x-0 top-1/2 -translate-y-1/2 h-[2px] rounded-full ${
              isRisk ? "border-t-2 border-dotted" : ""
            }`}
            style={{
              background: isRisk ? "transparent" : (forecast.confTone === "high" ? "#10B981" : "#94A3B8"),
              borderColor: isRisk ? (forecast.confTone === "low" ? "#F97316" : "#F59E0B") : "transparent",
            }}
            aria-hidden
          />
          {/* Risk zone highlight (medium risk + later half) */}
          {forecast.confTone === "medium" && (
            <div
              className="absolute top-1/2 -translate-y-1/2 h-3 rounded-md bg-amber-100/70 ring-1 ring-amber-200"
              style={{ left: "55%", right: "8%" }}
              data-testid={`sp-fc-bar-risk-zone-${forecast.id}`}
            >
              <span className="absolute inset-0 flex items-center justify-center text-[9px] font-bold text-amber-700 uppercase tracking-[0.06em]">
                Risk zone
              </span>
            </div>
          )}
          {/* Drift risk marker */}
          {drift && (
            <div
              className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 flex flex-col items-center"
              style={{ left: "50%" }}
              data-testid={`sp-fc-bar-drift-${forecast.id}`}
            >
              <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full ${forecast.confTone === "low" ? "bg-rose-100 text-rose-600 ring-1 ring-rose-300" : "bg-amber-100 text-amber-700 ring-1 ring-amber-300"}`}>
                <AlertTriangle className="h-3 w-3" strokeWidth={2.4} />
              </span>
              <div className="absolute top-full mt-1.5 whitespace-nowrap text-center leading-tight">
                <div className="text-[9.5px] font-semibold text-slate-700">Drift risk</div>
                <div className="text-[9px] text-slate-500 tabular-nums">Likely {fmtPillDate(drift.likelyIso)}</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Completion pill */}
      <div className="flex flex-col items-center shrink-0" data-testid={`sp-fc-bar-completion-${forecast.id}`}>
        <span className={`inline-flex items-center gap-1 text-[10px] font-semibold ${tone.text} leading-tight`}>
          <span className={`w-2.5 h-2.5 rounded-full ring-2 ring-white shadow-[0_0_0_1px_rgba(15,23,42,0.06)] ${
            forecast.confTone === "high" ? "bg-emerald-500" : forecast.confTone === "medium" ? "bg-amber-500" : forecast.confTone === "low" ? "bg-rose-500" : "bg-slate-300"
          }`} aria-hidden />
          Completion
        </span>
        <span className="text-[10px] text-slate-500 tabular-nums mt-0.5">
          {fmtPillDate(forecast.completionIso)}
        </span>
      </div>
    </div>
  );
}

/* ─── Confidence donut (SVG · iter-221B) ───────────────────────────── */
function ConfidenceDonut({ score, tone }) {
  const t = CONF_TONES[tone] || CONF_TONES.none;
  const lab = CONF_LABEL[tone] || CONF_LABEL.none;
  const pct = Math.max(0, Math.min(100, Number(score) || 0));
  const radius = 18;
  const circ = 2 * Math.PI * radius;
  const dashLen = (pct / 100) * circ;
  const arcColor =
    tone === "high"   ? "#10B981" :
    tone === "medium" ? "#F59E0B" :
    tone === "low"    ? "#F97316" :
                        "#CBD5E1";
  return (
    <div className="flex items-center gap-2.5" data-testid="sp-fc-confidence-donut">
      <div className="relative h-11 w-11 shrink-0">
        <svg viewBox="0 0 48 48" className="h-full w-full -rotate-90">
          <circle cx="24" cy="24" r={radius} fill="none" stroke="#F1F5F9" strokeWidth="4" />
          <circle
            cx="24" cy="24" r={radius}
            fill="none"
            stroke={arcColor}
            strokeWidth="4"
            strokeLinecap="round"
            strokeDasharray={`${dashLen} ${circ - dashLen}`}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={`text-[10px] font-bold tabular-nums ${t.text}`}>{score == null ? "—" : `${score}%`}</span>
        </div>
      </div>
      <div className="min-w-0 leading-tight">
        <div className={`text-[11.5px] font-semibold ${t.text}`}>{lab.label}</div>
        <div className="text-[9.5px] text-slate-400 truncate">{lab.sub}</div>
      </div>
    </div>
  );
}

/* Back-compat shim — older tests / call-sites may still reference the
 * legacy ConfidenceRing. Keep it but render the new donut. */
function ConfidenceRing({ score }) {
  return <ConfidenceDonut score={score} tone={confidenceTone(score)} />;
}

/* ─── Timeline row ─────────────────────────────────────────────────── */
function TimelineRow({ deal, forecast, onOpen }) {
  const noteTone = NOTE_TONE[forecast.statusNote] || NOTE_TONE["Moving"];
  return (
    <Link
      to={`/deal-rooms/${deal.id}`}
      onClick={onOpen}
      className="relative grid items-center gap-3 px-5 py-3.5 hover:bg-slate-50/60 transition-colors cursor-pointer group"
      style={{ gridTemplateColumns: `minmax(220px,260px) 130px repeat(${NUM_WEEKS}, minmax(0,1fr)) 110px 88px 28px` }}
      data-testid={`progression-row-${deal.id}`}
    >
      {/* iter-221B — Property & Parties cell: star · thumb · name +
          buyer/seller solicitor row + "+N" overflow. */}
      <div className="flex items-center gap-2.5 min-w-0">
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
          className="h-5 w-5 rounded text-slate-300 hover:text-amber-500 transition-colors flex items-center justify-center shrink-0"
          aria-label="Star deal"
          data-testid={`progression-row-star-${deal.id}`}
        >
          <Star className="h-3.5 w-3.5" />
        </button>
        <img
          src={forecast.thumb}
          onError={onPropertyImgError}
          alt=""
          className="h-10 w-10 rounded-lg object-cover bg-slate-100 shrink-0 ring-1 ring-slate-200/70"
          loading="lazy"
        />
        <div className="min-w-0 flex-1 flex flex-col leading-tight">
          <div className="text-[12px] font-semibold text-slate-900 truncate">{forecast.name}</div>
          <div className="flex items-center gap-1 mt-0.5 min-w-0">
            <span className="text-[10.5px] text-slate-500 truncate">{forecast.parties.buyerSol}</span>
            <span className="text-[10px] text-slate-300 shrink-0">↔</span>
            <span className="text-[10.5px] text-slate-500 truncate">{forecast.parties.sellerSol}</span>
            {forecast.parties.extra > 0 && (
              <span className="text-[9.5px] font-semibold text-slate-500 bg-slate-100 rounded px-1 py-px ml-0.5 shrink-0" data-testid={`progression-row-extra-${deal.id}`}>
                +{forecast.parties.extra}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* iter-221B — Stage cell: chip + status note with calm dot. */}
      <div className="flex flex-col gap-1 min-w-0" data-testid={`progression-row-stage-${deal.id}`}>
        <span className={`inline-flex items-center self-start rounded-md px-2 py-0.5 text-[10px] font-semibold ring-1 ${forecast.stageMeta.chip}`}>
          {forecast.stageMeta.label}
        </span>
        <span className={`inline-flex items-center gap-1.5 text-[10.5px] font-medium ${noteTone.text}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${noteTone.dot}`} aria-hidden />
          {forecast.statusNote}
        </span>
      </div>

      {/* Timeline bar (occupies weeks 1..NUM_WEEKS, spans dynamic) */}
      <TimelineBar forecast={forecast} />

      {/* Confidence donut (SVG · with subline) */}
      <div className="flex justify-center">
        <ConfidenceDonut score={forecast.confidence} tone={forecast.confTone} />
      </div>

      {/* Fee */}
      <div className="text-right text-[12.5px] font-semibold text-slate-900 tabular-nums" data-testid={`sp-fc-fee-${deal.id}`}>
        {fmtMoney(forecast.fee)}
      </div>

      {/* Kebab */}
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
        className="opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700 flex items-center justify-center"
        aria-label="Row actions"
        data-testid={`sp-fc-row-kebab-${deal.id}`}
      >
        <MoreHorizontal className="h-3 w-3" />
      </button>
    </Link>
  );
}

/* ─── Right rail: Exchange pressure donut ──────────────────────────── */
function ExchangePressureCard({ pressure }) {
  const total = pressure.total || 1;
  // SVG donut math — 5 buckets, each is a coloured arc.
  const buckets = [
    { id: "overdue", label: "Overdue",      value: pressure.overdue,  color: "#F43F5E" },
    { id: "due03",   label: "Due in 0–3 days",  value: pressure.due03,    color: "#F97316" },
    { id: "due47",   label: "Due in 4–7 days",  value: pressure.due47,    color: "#F59E0B" },
    { id: "due814",  label: "Due in 8–14 days", value: pressure.due814,   color: "#10B981" },
    { id: "due15",   label: "Due in 15+ days",  value: pressure.due15,    color: "#CBD5E1" },
  ];
  const circumference = 2 * Math.PI * 32; // radius 32
  let offset = 0;
  const segments = buckets.map((b) => {
    const pct = total ? b.value / total : 0;
    const length = pct * circumference;
    const segment = { ...b, length, offset, gap: circumference - length };
    offset += length;
    return segment;
  });

  return (
    <div
      className="rounded-2xl bg-white px-4 py-3.5"
      style={{ boxShadow: "inset 0 0 0 1px rgba(226,232,240,0.55), 0 1px 2px rgba(15,23,42,0.03)" }}
      data-testid="sp-fc-rail-pressure"
    >
      <div className="text-[12px] font-semibold text-slate-900 mb-3">Exchange pressure</div>
      <div className="flex items-start gap-4">
        <div className="relative h-[88px] w-[88px] shrink-0">
          <svg viewBox="0 0 80 80" className="h-full w-full -rotate-90" data-testid="sp-fc-pressure-donut">
            <circle cx="40" cy="40" r="32" fill="none" stroke="#F1F5F9" strokeWidth="9" />
            {segments.map((s) => (
              <circle
                key={s.id}
                cx="40" cy="40" r="32"
                fill="none"
                stroke={s.color}
                strokeWidth="9"
                strokeDasharray={`${s.length} ${s.gap}`}
                strokeDashoffset={-s.offset}
                strokeLinecap="butt"
              />
            ))}
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className="text-[18px] font-bold tabular-nums text-slate-900 leading-none">{total}</div>
            <div className="text-[9px] text-slate-400 mt-0.5">total</div>
          </div>
        </div>
        <ul className="flex-1 min-w-0 space-y-1.5">
          {buckets.map((b) => (
            <li key={b.id} className="flex items-center justify-between gap-2 text-[11px]" data-testid={`sp-fc-pressure-${b.id}`}>
              <div className="flex items-center gap-1.5 min-w-0">
                <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ background: b.color }} />
                <span className="text-slate-600 truncate">{b.label}</span>
              </div>
              <span className="text-slate-900 font-semibold tabular-nums">{b.value}</span>
            </li>
          ))}
        </ul>
      </div>
      <Link
        to="/insights"
        className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold text-violet-700 hover:text-violet-900"
        data-testid="sp-fc-pressure-report"
      >
        View pressure report <ArrowRight className="h-3 w-3" />
      </Link>
    </div>
  );
}

/* ─── Right rail: Upcoming exchanges ───────────────────────────────── */
function UpcomingExchangesCard({ upcoming }) {
  return (
    <div
      className="rounded-2xl bg-white px-4 py-3.5"
      style={{ boxShadow: "inset 0 0 0 1px rgba(226,232,240,0.55), 0 1px 2px rgba(15,23,42,0.03)" }}
      data-testid="sp-fc-rail-upcoming"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="text-[12px] font-semibold text-slate-900">Upcoming exchanges</div>
        <Link to="/calendar" className="text-[11px] font-semibold text-violet-700 hover:text-violet-900" data-testid="sp-fc-rail-view-calendar">
          View calendar
        </Link>
      </div>
      {upcoming.length === 0 ? (
        <div className="text-[11.5px] text-slate-400">No upcoming exchanges scheduled.</div>
      ) : (
        <ul className="space-y-2.5">
          {upcoming.map((u) => {
            const tone = u.daysOut <= 7  ? "bg-rose-500"
                       : u.daysOut <= 14 ? "bg-amber-500"
                       :                   "bg-emerald-500";
            return (
              <li key={u.id} className="flex items-center gap-3" data-testid={`sp-fc-upcoming-${u.id}`}>
                <div className="text-[11px] font-semibold text-slate-700 tabular-nums w-12 shrink-0">
                  {u.dateLabel}
                </div>
                <Link to={`/deal-rooms/${u.id}`} className="min-w-0 flex-1 hover:bg-slate-50 rounded -mx-1 px-1 py-0.5">
                  <div className="text-[12px] font-medium text-slate-800 truncate leading-tight">{u.name}</div>
                  <div className="text-[10.5px] text-slate-400 mt-0.5">{u.relative}</div>
                </Link>
                <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${tone}`} aria-hidden />
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/* ─── Right rail: Recent activity ──────────────────────────────────── */
const ACTIVITY_CATALOGUE = [
  { icon: Shield,   label: "AML verified",      tone: "emerald" },
  { icon: SearchIcon, label: "Search update",   tone: "violet"  },
  { icon: FileText, label: "Contract received", tone: "emerald" },
  { icon: Mail,     label: "Mortgage offer",    tone: "violet"  },
  { icon: HomeIcon, label: "Solicitor changed", tone: "amber"   },
];

function RecentActivityCard({ recent }) {
  return (
    <div
      className="rounded-2xl bg-white px-4 py-3.5"
      style={{ boxShadow: "inset 0 0 0 1px rgba(226,232,240,0.55), 0 1px 2px rgba(15,23,42,0.03)" }}
      data-testid="sp-fc-rail-recent"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="text-[12px] font-semibold text-slate-900">Recent activity</div>
        <Link to="/operations" className="text-[11px] font-semibold text-violet-700 hover:text-violet-900">View all</Link>
      </div>
      <ul className="space-y-2.5">
        {recent.map((a) => {
          const Icon = a.icon;
          const tile = a.tone === "emerald" ? "bg-emerald-50 text-emerald-600"
                     : a.tone === "violet"  ? "bg-violet-50 text-violet-600"
                     : a.tone === "amber"   ? "bg-amber-50 text-amber-700"
                     :                        "bg-slate-100 text-slate-600";
          return (
            <li key={a.id} className="flex items-start gap-3" data-testid={`sp-fc-recent-${a.id}`}>
              <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${tile}`}>
                <Icon className="h-3.5 w-3.5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-[12px] font-semibold text-slate-800 truncate">{a.label}</div>
                  <div className="text-[10.5px] text-slate-400 shrink-0 tabular-nums">{a.relative}</div>
                </div>
                <div className="text-[11px] text-slate-500 truncate mt-0.5">{a.deal}</div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/* ─── Legend footer ────────────────────────────────────────────────── */
function LegendFooter() {
  return (
    <div
      className="hidden md:flex items-center gap-x-5 gap-y-2 flex-wrap mt-4 px-1 text-[11px] text-slate-500"
      data-testid="sp-fc-legend"
    >
      <span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />High confidence (70%+)</span>
      <span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-amber-500" />Medium confidence (40–70%)</span>
      <span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-rose-500" />Low confidence (&lt;40%)</span>
      <span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-slate-300" />Not scheduled</span>
    </div>
  );
}

/* ═══ Page shell ════════════════════════════════════════════════════ */
export default function SalesProgressionForecastPage() {
  const navigate = useNavigate();
  const { deals, dealsLoading, findProperty } = useChainStore();
  const { user } = useAuth();

  /* Operator preference memory (iter-205C doctrine) — persist tab,
   * groupBy, viewMode per (workspace, user). */
  const { initial: prefs, persist } = useOperatorPrefs(
    "sales-progression-forecast",
    user?.org_id,
    user?.id,
  );

  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState(() => prefs.activeTab || "exchange");
  const [groupBy, setGroupBy]     = useState(() => prefs.groupBy || "week");
  const [viewMode, setViewMode]   = useState(() => prefs.viewMode || "timeline");

  useEffect(() => { persist({ activeTab }); }, [activeTab, persist]);
  useEffect(() => { persist({ groupBy   }); }, [groupBy,   persist]);
  useEffect(() => { persist({ viewMode  }); }, [viewMode,  persist]);

  /* The anchor = Monday of the current week. */
  const anchor = useMemo(() => startOfWeek(new Date()), []);

  /* Filtered deal set + per-row forecast annotations. */
  const allActive = useMemo(
    // Address from the linked property when the deal row doesn't carry one (wizard deals).
    () => (deals || []).filter((d) => !d.archived)
      .map((d) => (d.address || !d.propertyId ? d : { ...d, address: findProperty(d.propertyId)?.address || "" })),
    [deals, findProperty],
  );

  const annotated = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allActive
      .filter((d) => {
        if (!q) return true;
        const hay = `${d.name || ""} ${d.address || ""}`.toLowerCase();
        return hay.includes(q);
      })
      .map((d) => ({ d, fc: annotateForecast(d, anchor) }))
      .filter(({ fc }) => !!fc.exchangeIso)
      .sort((a, b) => new Date(a.fc.exchangeIso) - new Date(b.fc.exchangeIso));
  }, [allActive, anchor, search]);

  /* Per-week summaries (EXC + COMP + total fees). */
  const weekSummaries = useMemo(() => {
    const out = Array.from({ length: NUM_WEEKS }, () => ({ exc: 0, comp: 0 }));
    annotated.forEach(({ fc }) => {
      if (fc.xcWeek != null) out[fc.xcWeek].exc += 1;
      if (fc.ccWeek != null) out[fc.ccWeek].comp += 1;
    });
    return out;
  }, [annotated]);

  /* iter-221B — Fee total per week bucket (anchored to the exchange
   * week, so the forecasting lane reads as "this week we're
   * forecasting £X across N deals"). */
  const weekFees = useMemo(() => {
    const out = Array.from({ length: NUM_WEEKS }, () => 0);
    annotated.forEach(({ fc }) => {
      if (fc.xcWeek != null) out[fc.xcWeek] += Number(fc.fee || 0);
    });
    return out;
  }, [annotated]);

  /* Top KPI stats. */
  const kpis = useMemo(() => {
    const horizonEnd = addDays(anchor, 4 * 7).getTime();
    const inHorizon = annotated.filter(({ fc }) => new Date(fc.exchangeIso).getTime() <= horizonEnd);
    const exchanges = inHorizon.length;
    const completions = annotated.filter(({ fc }) => fc.completionIso && new Date(fc.completionIso).getTime() <= horizonEnd).length;
    const projectedFees = inHorizon.reduce((sum, { fc }) => sum + fc.fee, 0);
    const atRisk = inHorizon.filter(({ fc }) => fc.confTone === "medium" || fc.confTone === "low");
    const feesAtRisk = atRisk.reduce((sum, { fc }) => sum + fc.fee, 0);
    const _scored = inHorizon.filter(({ fc }) => fc.confidence != null);
    const weighted = _scored.length
      ? Math.round(_scored.reduce((s, { fc }) => s + fc.confidence, 0) / _scored.length)
      : 0;
    return {
      exchanges,
      exchangesDelta: 2,
      completions,
      completionsDelta: 1,
      projectedFees,
      projectedFeesDelta: 24_500,
      feesAtRisk,
      feesAtRiskDeals: atRisk.length,
      feesAtRiskDelta: 6_750,
      weightedConfidence: weighted,
      weightedConfidenceDelta: 3,
    };
  }, [annotated, anchor]);

  /* Focus strip stats. */
  const focusStats = useMemo(() => {
    const needAttention = annotated.filter(({ fc }) => fc.confTone === "medium" || fc.confTone === "low").length;
    const exchangesThisWeek = annotated.filter(({ fc }) => fc.xcWeek === 0).length;
    const silent = Math.max(2, Math.floor(annotated.length * 0.08));
    return { needAttention, exchangesThisWeek, silentCounterparties: silent };
  }, [annotated]);

  /* Exchange-pressure bucket counts. */
  const pressure = useMemo(() => {
    const now = Date.now();
    let overdue = 0, due03 = 0, due47 = 0, due814 = 0, due15 = 0;
    annotated.forEach(({ fc }) => {
      const d = Math.floor((new Date(fc.exchangeIso).getTime() - now) / _MS_PER_DAY);
      if (d < 0)      overdue += 1;
      else if (d <= 3)  due03 += 1;
      else if (d <= 7)  due47 += 1;
      else if (d <= 14) due814 += 1;
      else              due15 += 1;
    });
    return { overdue, due03, due47, due814, due15, total: overdue + due03 + due47 + due814 + due15 };
  }, [annotated]);

  /* Upcoming exchange list (5 soonest non-overdue). */
  const upcoming = useMemo(() => {
    const now = Date.now();
    return annotated
      .map(({ d, fc }) => {
        const daysOut = Math.floor((new Date(fc.exchangeIso).getTime() - now) / _MS_PER_DAY);
        return {
          id: d.id,
          name: fc.name,
          daysOut,
          dateLabel: fmtPillDate(fc.exchangeIso),
          relative: daysOut === 0 ? "Today" : daysOut === 1 ? "Tomorrow" : `In ${daysOut} days`,
        };
      })
      .filter((u) => u.daysOut >= 0)
      .slice(0, 5);
  }, [annotated]);

  /* Recent activity (5 most recent operational events from the activity
   * catalogue, deterministically distributed across the top deals). */
  const recent = useMemo(() => {
    return annotated.slice(0, 5).map(({ d, fc }, i) => {
      const ev = ACTIVITY_CATALOGUE[(_hash(d.id) + i) % ACTIVITY_CATALOGUE.length];
      const minsAgo = ((_hash(d.id + "act") % 360) + 5);
      const iso = new Date(Date.now() - minsAgo * 60_000).toISOString();
      return { id: d.id, ...ev, deal: fc.name, relative: relTimeFromIso(iso) };
    });
  }, [annotated]);

  /* Total fees of the current week's commencing summary. */
  const totalFees = annotated.reduce((s, { fc }) => s + fc.fee, 0);

  /* Today-line geometry — anchored to the centre of the "This week"
   * column. Computed via ref so the line tracks the actual grid. */
  const gridRef = useRef(null);
  const [todayLeftPx, setTodayLeftPx] = useState(0);
  useEffect(() => {
    const calc = () => {
      const grid = gridRef.current;
      if (!grid) return;
      const width = grid.clientWidth;
      // iter-221B grid: minmax(220,260) + 130 + N*flex + 110 + 88 + 28
      // with gap-3 (12px) between cells (8 gaps in the header).
      const padding = 20; // px-5 inside the grid container
      const propCol = 260;
      const stageCol = 130;
      const gap = 12;
      const left = padding + propCol + gap + stageCol + gap;
      const rightFixed = 110 + 88 + 28 + (3 * gap);
      const remaining = width - left - rightFixed - padding;
      const weekCol = remaining / NUM_WEEKS;
      // Today within "This week" — proportional to days since Monday.
      const dayInWeek = (new Date().getDay() + 6) % 7;
      const pos = left + (dayInWeek / 7) * weekCol;
      setTodayLeftPx(pos);
    };
    calc();
    window.addEventListener("resize", calc);
    return () => window.removeEventListener("resize", calc);
  }, [annotated.length]);

  const dateRangeLabel = useMemo(() => {
    const end = addDays(anchor, NUM_WEEKS * 7 - 1);
    const fmt = (d) => d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    return `${fmt(anchor)} – ${fmt(end)}`;
  }, [anchor]);

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar />
      <main
        className="md:ml-[var(--cf-sidebar-w,300px)] transition-[margin-left] duration-300 ease-out min-w-0 overflow-x-hidden"
        data-testid="sp-fc-main"
      >
        <div className={`${WORKSPACE_SHELL_CLASS} pb-24 md:pb-8`}>
          {/* Desktop chrome — mobile fallback keeps the existing
              SalesProgressionMobile experience (unchanged by iter-208). */}
          <ForecastHeader
            search={search}
            setSearch={setSearch}
            dateRangeLabel={dateRangeLabel}
          />

          <KpiStrip kpis={kpis} />

          <FocusStrip stats={focusStats} />

          <ForecastTabs
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            groupBy={groupBy}
            setGroupBy={setGroupBy}
            viewMode={viewMode}
          />

          {/* Two-column body: timeline + right rail. iter-208P — rail
              tightened to 280px so the timeline grid genuinely dominates. */}
          <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_280px] gap-5">
            <div className="min-w-0">
              <div
                ref={gridRef}
                className="relative rounded-2xl bg-white overflow-hidden"
                style={{ boxShadow: "inset 0 0 0 1px rgba(226,232,240,0.6), 0 1px 2px rgba(15,23,42,0.03)" }}
                data-testid="sp-fc-timeline-grid"
              >
                <WeekHeader
                  anchor={anchor}
                  weekSummaries={weekSummaries}
                  weekFees={weekFees}
                  totalActive={annotated.length}
                  totalFees={totalFees}
                />
                <ColumnSubheaders />
                <TodayLine leftPx={todayLeftPx} />
                {dealsLoading ? (
                  <div className="p-10 text-center text-[12.5px] text-slate-400">Loading forecasting telemetry…</div>
                ) : annotated.length === 0 ? (
                  <div className="p-10 text-center text-[12.5px] text-slate-500" data-testid="sp-fc-empty">
                    No active transactions in this forecast horizon.
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100" data-testid="sp-fc-rows">
                    {annotated.slice(0, 8).map(({ d, fc }) => (
                      <TimelineRow key={d.id} deal={d} forecast={fc} />
                    ))}
                  </div>
                )}
              </div>

              <LegendFooter />

              <div
                className="hidden md:flex items-center justify-between mt-4 px-1"
                data-testid="sp-fc-footer"
              >
                <div />
                <div className="flex items-center gap-3">
                  <span className="text-[11.5px] text-slate-500" data-testid="sp-fc-footer-count">
                    Showing {Math.min(annotated.length, 8)} of {annotated.length} active deals
                  </span>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1.5 rounded-lg ring-1 ring-slate-200 hover:bg-slate-50 bg-white px-3 py-1.5 text-[11.5px] font-semibold text-slate-700 cf-press"
                    data-testid="sp-fc-load-more"
                  >
                    Load more <ChevronDown className="h-3 w-3" />
                  </button>
                </div>
                <div className="text-[10.5px] text-slate-400 inline-flex items-center gap-1.5">
                  <CircleDot className="h-3 w-3" /> All times are relative
                </div>
              </div>
            </div>

            {/* Right rail. iter-208P — tighter spacing + softer
                surfaces so the rail supports the timeline rather
                than competing with it. */}
            <aside className="hidden xl:block space-y-3" data-testid="sp-fc-right-rail">
              <ExchangePressureCard pressure={pressure} />
              <UpcomingExchangesCard upcoming={upcoming} />
              <RecentActivityCard recent={recent} />
            </aside>
          </div>
        </div>
      </main>
    </div>
  );
}
